import React, { useEffect, useRef, useState } from 'react';
import './scenemenu.css';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddToPhotosOutlinedIcon from '@mui/icons-material/AddToPhotosOutlined';
import CompareOutlinedIcon from '@mui/icons-material/CompareOutlined';
import StartOutlinedIcon from '@mui/icons-material/StartOutlined';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import sceneIcon from '../../assets/scene-icon.png';
import { getAssetDownloadUrl, isVideoMediaUrl } from '../../services/assetUrlUtils';
import { canonicalVideoKey } from '../canvas/utils/videoPreloader';
import { getVideoPoster } from '../canvas/utils/videoPosterCache';
import { isHiddenTilesEnabled } from '../../services/featureFlagsService';

interface SceneMenuProps {
  videoSas?: {
    token?: string;
    expiredAt?: number;
  };
  scenes: {
    id: string;
    name: string;
    backgroundId?: string;
    backgroundImage?: string;
    duration?: number;
  }[];
  onAddScene: () => void;
  onSelectScene: (sceneId: string) => void;
  selectedSceneId: string | null;
  onUpdateSceneName: (sceneId: string, newName: string) => void;
  onDeleteScene: (sceneId: string) => void;
  onDuplicateScene: (sceneId: string) => void;
  onTransitionScene: (sceneId: string) => void;
  onSceneDrag: (draggedIndex: number, droppedIndex: number) => void;
  formatDuration: (seconds: number) => string;
}

const ItemType = 'SCENE';

const SceneThumbnailMedia: React.FC<{ src: string }> = ({ src }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  // Poster-first: a captured first frame paints with zero network (shared
  // canonical-key cache, immune to SAS refreshes) and avoids a metadata fetch
  // per scene. Otherwise the original static video preview below applies.
  const poster = getVideoPoster(canonicalVideoKey(src));

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // A previously failed load hides the element (see onError below). Restore
    // visibility whenever the source changes so a re-signed URL (e.g. after
    // the video's SAS token was refreshed) can recover the thumbnail without
    // a remount.
    video.style.display = '';

    video.pause();
    try {
      video.currentTime = 0;
    } catch {
      // Some browsers refuse to seek until metadata has loaded.
    }
  }, [src]);

  if (poster) {
    return (
      <img
        className="scene-thumbnail-image"
        src={poster}
        alt=""
        draggable={false}
      />
    );
  }

  return (
    <video
      ref={videoRef}
      className="scene-thumbnail-video"
      src={src}
      muted
      playsInline
      preload="metadata"
      onLoadedData={(e) => {
        const video = e.currentTarget;
        try {
          video.currentTime = 0;
        } catch {
          // Ignore seek errors on slow-loading media.
        }
        video.pause();
      }}
      onError={(e) => {
        e.currentTarget.style.display = 'none';
      }}
    />
  );
};

const DraggableSceneCard: React.FC<{
  scene: {
    id: string;
    name: string;
    backgroundId?: string;
    backgroundImage?: string;
    duration?: number;
  };
  index: number;
  isSelected: boolean;
  canDeleteScene: boolean;
  onSelectScene: (sceneId: string) => void;
  onUpdateSceneName: (sceneId: string, newName: string) => void;
  onDeleteScene: (sceneId: string) => void;
  onDuplicateScene: (sceneId: string) => void;
  onTransitionScene: (sceneId: string) => void;
  onSceneDrag: (draggedIndex: number, droppedIndex: number) => void;
  editingSceneId: string | null;
  editingName: string;
  videoSas?: {
    token?: string;
    expiredAt?: number;
  };
  onNameClick: (
    e: React.MouseEvent,
    sceneId: string,
    currentName: string
  ) => void;
  onNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onNameSubmit: (
    e: React.KeyboardEvent<HTMLInputElement>,
    sceneId: string
  ) => void;
  onNameBlur: (sceneId: string) => void;
  formatDuration: (seconds: number) => string;
}> = ({
  scene,
  index,
  isSelected,
  canDeleteScene,
  onSelectScene,
  onDeleteScene,
  onDuplicateScene,
  onTransitionScene,
  onSceneDrag,
  editingSceneId,
  editingName,
  onNameClick,
  onNameChange,
  onNameSubmit,
  onNameBlur,
  formatDuration,
  videoSas,
}) => {
  const resolvedBackground = getAssetDownloadUrl(
    scene.backgroundImage,
    videoSas?.token
  );
  const hasVideoThumbnail = isVideoMediaUrl(resolvedBackground);

  const [{ isDragging }, dragRef] = useDrag({
    type: ItemType,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, dropRef] = useDrop({
    accept: ItemType,
    hover: (draggedItem: { index: number }) => {
      if (draggedItem.index !== index) {
        onSceneDrag(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });
  return (
    <div
      ref={(node) => dragRef(dropRef(node))}
      className={`scene-card ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''}`}
      onClick={() => onSelectScene(scene.id)}
    >
      <div className={`scene-card-container ${isSelected ? 'selected' : ''}`}>
        <div className="scene-thumbnail">
          {hasVideoThumbnail ? (
            <SceneThumbnailMedia src={resolvedBackground} />
          ) : resolvedBackground ? (
            <img
              className="scene-thumbnail-image"
              src={resolvedBackground}
              alt={`${scene.name} thumbnail`}
              draggable={false}
              onLoad={(e) => {
                // Restore visibility if a previous src (e.g. signed with an
                // expired SAS token) failed and hid the element; the fresh
                // URL may now load fine.
                e.currentTarget.style.display = '';
              }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div className="scene-thumbnail-placeholder" />
          )}
          <div className="scene-duration">
            {scene.duration ? formatDuration(scene.duration) : '00:00'}
          </div>
        </div>
        <div className="scene-card-footer">
          {editingSceneId === scene.id ? (
            <input
              type="text"
              value={editingName}
              onChange={onNameChange}
              onKeyDown={(e) => onNameSubmit(e, scene.id)}
              onBlur={() => onNameBlur(scene.id)}
              className="scene-name-input"
              autoFocus
            />
          ) : (
            <span
              className="scene-name-draggable"
              onClick={(e) => onNameClick(e, scene.id, scene.name)}
            >
              {scene.name}
            </span>
          )}
          <div className="scene-controls">
            <button
              className="scene-control-btn delete"
              disabled={!canDeleteScene}
              onClick={(e) => {
                e.stopPropagation();
                if (!canDeleteScene) return;
                onDeleteScene(scene.id);
              }}
              title={
                canDeleteScene
                  ? 'Delete scene'
                  : 'At least one scene is required'
              }
            >
              <DeleteOutlineIcon style={{ fontSize: '1.5rem' }} />
            </button>
            <button
              className="scene-control-btn duplicate"
              onClick={(e) => {
                e.stopPropagation();
                onDuplicateScene(scene.id);
              }}
              title="Duplicate scene"
            >
              <AddToPhotosOutlinedIcon style={{ fontSize: '1.2rem' }} />
            </button>
            <button
              className="scene-control-btn transition"
              onClick={(e) => {
                e.stopPropagation();
                onTransitionScene(scene.id);
              }}
              title="Add transition"
            >
              <StartOutlinedIcon style={{ fontSize: '1.5rem' }} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SceneMenu: React.FC<SceneMenuProps> = ({
  scenes,
  onAddScene,
  onSelectScene,
  selectedSceneId,
  onUpdateSceneName,
  onDeleteScene,
  onDuplicateScene,
  onTransitionScene,
  onSceneDrag,
  formatDuration,
  videoSas,
}) => {
  const [editingSceneId, setEditingSceneId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  // Manual scene creation is hidden when the hidden_tiles flag is on (same
  // flag that hides the asset tiles); scenes then come only from video gen.
  const showAddScene = !isHiddenTilesEnabled();

  const handleNameClick = (
    e: React.MouseEvent,
    sceneId: string,
    currentName: string
  ) => {
    e.stopPropagation();
    setEditingSceneId(sceneId);
    setEditingName(currentName);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingName(e.target.value);
  };

  const handleNameSubmit = (
    e: React.KeyboardEvent<HTMLInputElement>,
    sceneId: string
  ) => {
    if (e.key === 'Enter') {
      onUpdateSceneName(sceneId, editingName);
      setEditingSceneId(null);
    } else if (e.key === 'Escape') {
      setEditingSceneId(null);
    }
  };

  const handleNameBlur = (sceneId: string) => {
    if (editingName.trim()) {
      onUpdateSceneName(sceneId, editingName);
    }
    setEditingSceneId(null);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const renderCompactView = () => (
    <div className="scene-menu">
      <div className="scene-menu-header" onClick={toggleExpanded}>
        <img src={sceneIcon} alt="scene" />
        <h2 className="scene-menu-title">Scenes</h2>
      </div>
      <div className="scenes-list">
        {scenes.map((scene, index) => (
          <div
            key={scene.id}
            className={`scene-item ${selectedSceneId === scene.id ? 'selected' : ''}`}
            onClick={() => onSelectScene(scene.id)}
          >
            <div className="scene-icon">
              <div className="scene-number">{index + 1}</div>
            </div>
            {editingSceneId === scene.id ? (
              <input
                type="text"
                value={editingName}
                onChange={handleNameChange}
                onKeyDown={(e) => handleNameSubmit(e, scene.id)}
                onBlur={() => handleNameBlur(scene.id)}
                className="scene-name-input"
                autoFocus
              />
            ) : (
              <span
                className="scene-name"
                onClick={(e) => handleNameClick(e, scene.id, scene.name)}
              >
                {scene.name}
              </span>
            )}
          </div>
        ))}
        {showAddScene && (
          <button className="add-scene-button" onClick={onAddScene}>
            <span>+</span> Add scene
          </button>
        )}
      </div>
    </div>
  );

  const renderExpandedView = () => (
    <div className="scene-menu expanded">
      <div className="scene-menu-header" onClick={toggleExpanded}>
        <CompareOutlinedIcon style={{ fontSize: '1.5rem' }} />
        <h2 className="scene-menu-title-expanded">Scenes</h2>
      </div>
      <div className="scenes-grid">
        {scenes.map((scene, index) => (
          <DraggableSceneCard
            key={scene.id}
            scene={scene}
            index={index}
            isSelected={selectedSceneId === scene.id}
            canDeleteScene={scenes.length > 1}
            onSelectScene={onSelectScene}
            onUpdateSceneName={onUpdateSceneName}
            onDeleteScene={onDeleteScene}
            onDuplicateScene={onDuplicateScene}
            onTransitionScene={onTransitionScene}
            onSceneDrag={onSceneDrag}
            editingSceneId={editingSceneId}
            editingName={editingName}
            videoSas={videoSas}
            onNameClick={handleNameClick}
            onNameChange={handleNameChange}
            onNameSubmit={handleNameSubmit}
            onNameBlur={handleNameBlur}
            formatDuration={formatDuration}
          />
        ))}
        {showAddScene && (
          <button className="add-scene-card" onClick={onAddScene}>
            <span>+</span>
            <span>Add scene</span>
          </button>
        )}
      </div>
    </div>
  );

  return (
    <DndProvider backend={HTML5Backend}>
      {isExpanded ? renderExpandedView() : renderCompactView()}
    </DndProvider>
  );
};

export default SceneMenu;
